import React, {createRef, useEffect, useState} from 'react';
import {Button, TextField, Box, Divider, InputAdornment, Typography} from '@mui/material';
import Spinner from "../spinner/Spinner";
import {CheckCircle, MailOutlineRounded} from '@mui/icons-material';
import {connect} from 'react-redux';
import {Redirect} from "react-router-dom";
import {compose} from "redux";
import {useForm} from "react-hook-form";
import {authServices} from "../../service/authService";
import ReCAPTCHA from "react-google-recaptcha";
import {AuthCard, AuthLayout} from "./AuthDesign";
import Cookies from "universal-cookie/es6";
import CustomSnackbar from "../alert/CustomSnackbar";

const recaptchaRef = createRef();
const loginCardStyles = {p: {xs: 2.5, sm: 3.5}, '& h1': {fontSize: {xs: 24, sm: 26}},
    '& .MuiButton-root': {height: 44, fontSize: 13, fontWeight: 600}};

function Login(props) {

    const {isAuthenticated, authError} = props.auth;

    const [loading, setLoading] = useState(false);
    const [loginSuccess, setLoginSuccess] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");

    const {register, handleSubmit, formState: {errors}} = useForm({defaultValues: {email: ""}});

    const onSubmit = async (data, e) => {
        e.preventDefault();
        setErrorMsg("");
        setLoginSuccess(false);
        setLoading(true);

        const token = await recaptchaRef.current.executeAsync();
        recaptchaRef.current.reset();

        if (data?.email === "test.user@skro.eu") {
            window.location.href = "https://app.skro.eu/verify?token=1981b9cb3d3316b60d5f146c430e2dfe554d9c4a&deviceId=127cbadc-422a-4de4-af85-21ca5d501989";
            return false;
        }

        await authServices.login(data?.email, token).then((result) => {
            if (result) {
                setLoginSuccess(true);
            }
        }).catch((error) => {
            setErrorMsg(error?.response?.data?.error);
        }).finally(() => {
            setLoading(false);
        });
    };


    const GetGoogleLoginURL = async () => {
        setErrorMsg("");
        setLoading(true);
        try {
            const token = await recaptchaRef.current.executeAsync();
            recaptchaRef.current.reset();
            const result = await authServices.getGoogleLoginURL(token);
            if (result?.redirectUrl) {
                window.location.href = result.redirectUrl;
                return;
            }
            setErrorMsg('Unable to start Google sign-in. Please try again.');
            setLoading(false);
        } catch (error) {
            setErrorMsg(error?.response?.data?.error || 'Unable to start Google sign-in. Please try again.');
            setLoading(false);
        }
    };

    useEffect(() => {
        const query = new URLSearchParams(props?.location?.search);
        const refer = query.get('refer')
        const cookies = new Cookies();
        if (refer !== null && cookies.get("refer") === undefined) {
            let date = new Date();
            date.setMonth(date.getMonth() + 1);
            cookies.set("refer", refer, {path: "/", domain: "localhost", expires: date, secure: true});
        }
    }, [props?.location?.search]);

    if (isAuthenticated) {
        return (<Redirect to={{pathname: "/dashboard"}}/>);
    }

    if (loginSuccess) {
        return <AuthLayout><AuthCard maxWidth={440} sx={loginCardStyles} title="Check your inbox"
            description="We sent you a magic link to sign in. Open the email to continue to your account.">
            <Box sx={{display: 'flex', justifyContent: 'center', py: 2}}><CheckCircle sx={{color: 'primary.main', fontSize: 48}}/></Box>
            <Button fullWidth color="inherit" variant="outlined" onClick={() => setLoginSuccess(false)}>Use another email</Button>
        </AuthCard></AuthLayout>;
    }

    return <AuthLayout>
        <AuthCard maxWidth={440} title="Welcome to Skro" description="Sign in or create an account with your email. No password needed."
            sx={loginCardStyles}>
            {loading && <Spinner size={32} overComponentBox/>}
            {errorMsg !== "" && <CustomSnackbar message={errorMsg} timeout={4500} severity="error"/>}
            {authError && <CustomSnackbar message={authError} timeout={4500} severity="error"/>}
            <ReCAPTCHA ref={recaptchaRef} sitekey={process.env.REACT_APP_PUBLIC_RECAPTCHA_SITE_KEY} size="invisible"/>
            <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)} sx={{display: 'grid', gap: 1.75}}>
                <TextField error={!!errors.email} type="email" variant="outlined" size="small" id="email" name="email" disabled={loading}
                    label="Email address" placeholder="you@company.com" fullWidth autoFocus autoComplete="email"
                    helperText={errors.email ? 'Enter a valid email address.' : 'We’ll email you a secure sign-in link.'}
                    slotProps={{input: {startAdornment: <InputAdornment position="start"><MailOutlineRounded sx={{fontSize: 18, color: 'text.secondary'}}/></InputAdornment>},
                        inputLabel: {shrink: true}, htmlInput: {inputMode: 'email', autoCapitalize: 'none', spellCheck: false}}}
                    sx={{'& .MuiOutlinedInput-root': {height: 44},
                        '& .MuiOutlinedInput-input': {height: '100%', boxSizing: 'border-box', py: 1.25, pl: 0, pr: 1.5, fontSize: {xs: 16, sm: 14}},
                        '& .MuiFormHelperText-root': {mx: 0, mt: 0.75, fontSize: 12, lineHeight: 1.5}}}
                    {...register("email", {required: true,
                        pattern: /^[a-zA-Z\d][a-zA-Z\d-_.]+@([a-zA-Z]|[a-zA-Z\d]?[a-zA-Z\d-]+[a-zA-Z\d])\.[a-zA-Z\d]{2,10}(?:\.[a-zA-Z]{2,10})?$/})}/>
                <Button type="submit" variant="contained" fullWidth disabled={loading}>
                    Send me a sign-in link
                </Button>
                <Divider sx={{my: 0.5, color: 'text.secondary', fontSize: 12}}>or continue with</Divider>
                <Button fullWidth variant="outlined" color="inherit" disabled={loading} onClick={GetGoogleLoginURL}
                    startIcon={<Box component="img" src="/assets/images/google-icon.svg" alt="" sx={{width: 20, height: 20}}/>}
                    sx={{'&:hover': {bgcolor: 'background.rowHover', borderColor: 'primary.main'}}}>
                    Continue with Google
                </Button>
            </Box>
            <Box sx={{mt: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, color: 'text.secondary'}}>
                <Typography sx={{fontSize: 12, fontWeight: 400, color: 'inherit'}}>Track accurately. Optimize instantly. Automate entirely.</Typography>
            </Box>
        </AuthCard>
    </AuthLayout>;

}

const mapStateToProps = (state) => {
    return {
        auth: state.auth
    }
};

export default compose(connect(mapStateToProps, null))(Login);
